import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Github } from "lucide-react";
import { Reveal, SectionHeader, StatusBadge } from "./shared";
import { projects, projectCategories } from "../../data/portfolioData";
import { ProjectDetailModal } from "./ProjectDetailModal";

export const FeaturedWork = () => {
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);

  const visible = filter === "all" ? projects : projects.filter((p) => p.category === filter);

  return (
    <section id="work" className="py-20 lg:py-28" data-testid="featured-work-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          index="03"
          eyebrow="Featured Work"
          title="Selected Work"
          description="Production systems, client products and personal builds — each one shipped, each one solving a real problem."
          testId="work-header"
        />

        <Reveal>
          <div className="flex flex-wrap gap-2.5 mb-10" data-testid="project-filters">
            {projectCategories.map((c) => (
              <button
                key={c.id}
                onClick={() => setFilter(c.id)}
                className={`rounded-full px-4.5 px-5 py-2 text-xs sm:text-sm font-medium transition-all duration-300 border ${
                  filter === c.id
                    ? "bg-amber-400 text-[#07090E] border-amber-400"
                    : "border-white/10 text-slate-400 hover:text-slate-100 hover:border-white/25"
                }`}
                data-testid={`filter-${c.id}`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="grid md:grid-cols-2 gap-6 lg:gap-8">
          <AnimatePresence mode="popLayout">
            {visible.map((p) => (
              <motion.article
                layout
                key={p.slug}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="group relative rounded-2xl border border-white/[0.07] bg-[#0F1420] overflow-hidden transition-colors duration-500 hover:border-white/[0.14]"
                data-testid={`project-card-${p.slug}`}
              >
                <div className="relative h-56 sm:h-64 overflow-hidden">
                  <img
                    src={p.image}
                    alt={`${p.title} project mockup`}
                    loading="lazy"
                    className="h-full w-full object-cover opacity-70 transition-all duration-700 group-hover:scale-105 group-hover:opacity-90"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F1420] via-[#0F1420]/30 to-transparent" />
                  <div className="absolute top-4 left-4 flex gap-2">
                    <StatusBadge status={p.status} />
                    <span className="rounded-full border border-white/10 bg-black/40 backdrop-blur px-2.5 py-0.5 font-code text-[11px] text-slate-300">
                      {p.year}
                    </span>
                  </div>
                </div>

                <div className="p-6 sm:p-7">
                  <p className="font-code text-[11px] uppercase tracking-[0.2em] text-amber-400/80">{p.type}</p>
                  <h3 className="mt-2 font-display text-xl sm:text-2xl font-semibold tracking-tight text-slate-50">{p.title}</h3>
                  <p className="mt-2.5 text-sm text-slate-400 leading-relaxed">{p.description}</p>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {p.technologies.slice(0, 4).map((t) => (
                      <span key={t} className="rounded-md border border-white/[0.08] bg-white/[0.03] px-2.5 py-1 text-[11px] text-slate-400">
                        {t}
                      </span>
                    ))}
                  </div>

                  <div className="mt-6 flex items-center gap-3">
                    <button
                      onClick={() => setActive(p)}
                      className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.06] border border-white/10 px-4 py-2 text-xs font-semibold text-slate-200 transition-all duration-300 hover:bg-amber-400 hover:text-[#07090E] hover:border-amber-400"
                      data-testid={`project-view-${p.slug}`}
                    >
                      View Project <ArrowUpRight size={13} />
                    </button>
                    {p.github && (
                      <a
                        href={p.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-xs font-semibold text-slate-400 transition-colors hover:text-slate-100 hover:border-white/25"
                        data-testid={`project-github-${p.slug}`}
                      >
                        <Github size={13} /> Code
                      </a>
                    )}
                    {p.playStore && (
                      <a
                        href={p.playStore}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/20 px-4 py-2 text-xs font-semibold text-emerald-300/90 transition-colors hover:bg-emerald-400/10 hover:border-emerald-400/40"
                        data-testid={`project-playstore-${p.slug}`}
                      >
                        <ArrowUpRight size={13} /> Play Store
                      </a>
                    )}
                  </div>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <ProjectDetailModal project={active} onClose={() => setActive(null)} />
    </section>
  );
};
